import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';

import Avatar, { AVATAR_SIZES } from '@jobcase/react_pattern_library/components/Avatar';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon';
import TimesRebrandLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/TimesRebrandLightSVG';
import rebrandStyles from '@jobcase/react_pattern_library/styles/rebrand';

import { getCurrentProfile } from '../../../redux/nodes/authentication/selectors';
import { getUnseenCount } from '../../../redux/nodes/entities/notifications/selectors';
import { routeToProfile } from '../../../redux/nodes/router/actions';

import { fullName } from '../../../utils/profile';
import { getMenuItems } from '../../../utils/employerNavigationRebrand';

import config from '../../../config';

const TertiaryNavigationContainer = styled.aside`
  background-color: ${rebrandStyles.color.jobcaseWhiteColor};
  color: ${rebrandStyles.color.titleTextColor};
  display: flex;
  flex-direction: column;
  font-family: ${rebrandStyles.font.fontFamily};
  font-size: ${rebrandStyles.font.fontSize.body2};
  height: 100vh;
  position: fixed;
  right: ${({ isOpen }) => (isOpen ? '0' : '-85%')};
  top: 0;
  transition: right .4s;
  width: 85%;
  z-index: 200;
`;

const SideNavMask = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  height: 100%;
  left: 0;
  overflow-y: auto;
  position: fixed;
  top: 0;
  transition: background .4s;
  width: 100%;
  z-index: 100;

  @media(min-width: ${rebrandStyles.breakpoint.md}) {
    display: none;
  }
`;

const SVGCloseIcon = styled(SVGIcon)`
  cursor: pointer;
  font-size: ${rebrandStyles.font.fontSize.h2};
  margin-left: ${rebrandStyles.spacing.extraTiny - 2}px;
  width: 20px;

  path {
    fill: ${rebrandStyles.color.jobcaseBlackColor};
  }
`;

const StyledOptionsTypography = styled.div`
  color: ${rebrandStyles.color.jobcaseBlackColor};
  cursor: pointer;
  font-family: ${rebrandStyles.font.fontFamily};
  font-size: ${rebrandStyles.font.fontSize.body};
  font-weight: 600;
  line-height: ${rebrandStyles.font.lineHeight.body};
  padding: ${rebrandStyles.spacing.tiny}px;

  ${({ hasTopDivider }) => hasTopDivider && css`
    border-top: 1px solid ${rebrandStyles.color.dividerGrayColor};
  `}
`;

const StyledProfileInfoOuterWrapper = styled.div`
  align-items: center;
  border-bottom: 1px solid ${rebrandStyles.color.dividerGrayColor};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${rebrandStyles.spacing.extraTiny + 2}px ${rebrandStyles.spacing.tiny}px;

  .tertiary-nav-avatar-img-container {
    margin-right: 0;
  }
`;

const TertiaryNavigation = ({
  toggleOpen,
  isOpen,
}) => {
  const currentProfile = useSelector(getCurrentProfile);

  const unseenNotificationCount = useSelector(getUnseenCount);

  const MENU_ITEMS = getMenuItems(unseenNotificationCount);

  const handleOnClickViewProfile = () => {
    routeToProfile();
  };

  const ALL_MENU_ITEMS = [{
    onClick: handleOnClickViewProfile,
    text: 'View Profile',
  },
  ...MENU_ITEMS,
  ];

  return (
    <>
      <TertiaryNavigationContainer isOpen={isOpen}>
        <StyledProfileInfoOuterWrapper>
          <SVGCloseIcon
            svgChild={TimesRebrandLightSVG}
            onClick={toggleOpen}
            size={SVGIcon.ICON_SIZE.X1}
          />
          <Avatar
            altText={(currentProfile && currentProfile?.profile_photo) ? `${fullName(currentProfile)} Profile Picture` : 'Placeholder Profile Picture'}
            imageSrc={currentProfile?.profile_photo || '/static-ec/profile-placeholder.png'}
            onClickAvatar={handleOnClickViewProfile}
            resizeImage={currentProfile?.profile_photo && config.cloudinaryEnabled}
            sizeVariant={AVATAR_SIZES.SMALL}
            classNames={{
              ImageContainer: 'tertiary-nav-avatar-img-container',
            }}
          />
        </StyledProfileInfoOuterWrapper>
        {ALL_MENU_ITEMS.map(({ onClick, text, hasTopDivider }) => (
          <StyledOptionsTypography
            onClick={onClick}
            hasTopDivider={hasTopDivider}
            key={text}
          >
            {text}
          </StyledOptionsTypography>
        ))}
      </TertiaryNavigationContainer>
      {isOpen && (
        <SideNavMask onClick={toggleOpen} />
      )}
    </>
  );
};

TertiaryNavigation.propTypes = {
  toggleOpen: PropTypes.func,
  isOpen: PropTypes.bool,
};

TertiaryNavigation.defaultProps = {
  toggleOpen: () => {},
  isOpen: false,
};

export default TertiaryNavigation;
